"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOffsetLocations = void 0;
const react_1 = require("react");
const constants_1 = require("../../lib/constants");
const useOffsetLocations = (locations, showMultipleMarkersForSameLocation) => {
    const normalizeCoordinates = (0, react_1.useCallback)((coordinates) => {
        if (Array.isArray(coordinates)) {
            return { lng: coordinates[0], lat: coordinates[1] };
        }
        if (coordinates &&
            typeof coordinates === 'object' &&
            'lat' in coordinates &&
            'lng' in coordinates) {
            return { lng: coordinates.lng, lat: coordinates.lat };
        }
        return null;
    }, []);
    // Converts polar coordinates (r, θ) to cartesian (x, y) using:
    // x = r * cos(θ) <- horizontal displacement
    // y = r * sin(θ) <- vertical displacement
    //
    // More context on this approach:
    // Imagine drawing a circle around the original point. This calculates
    // where to place a new point on that circle's edge at a specific angle
    // angle is already calculated based on the number of locations at the same point
    const applyOffset = (0, react_1.useCallback)((coords, angle, distance) => ({
        lng: coords.lng + distance * Math.cos(angle),
        lat: coords.lat + distance * Math.sin(angle)
    }), []);
    const reconstructCoordinates = (0, react_1.useCallback)((coords, originalCoords) => {
        if (Array.isArray(originalCoords)) {
            return [coords.lng, coords.lat];
        }
        return coords;
    }, []);
    return (0, react_1.useMemo)(() => {
        if (!showMultipleMarkersForSameLocation) {
            return locations;
        }
        const coordinateGroups = new Map();
        locations.forEach((location) => {
            var _a;
            if (location.coordinates) {
                const normalizedCoords = normalizeCoordinates(location.coordinates);
                if (normalizedCoords) {
                    const coordKey = `${normalizedCoords.lng},${normalizedCoords.lat}`;
                    if (!coordinateGroups.has(coordKey)) {
                        coordinateGroups.set(coordKey, []);
                    }
                    (_a = coordinateGroups.get(coordKey)) === null || _a === void 0 ? void 0 : _a.push(location);
                }
            }
        });
        const offsetLocations = [];
        coordinateGroups.forEach((locationsAtCoord) => {
            if (locationsAtCoord.length === 1) {
                offsetLocations.push(locationsAtCoord[0]);
            }
            else {
                locationsAtCoord.forEach((location, index) => {
                    // This angle is calculated based on the number of the locations.
                    // For example in simple case of 2 locations, the angles will be 0 and π (180 degrees)
                    // in case of 4 it will be 90 degrees apart (0, π/2, π, 3π/2)
                    // converting this to radians for calculations
                    const angle = (index * 2 * Math.PI) / locationsAtCoord.length;
                    const normalizedCoords = normalizeCoordinates(location.coordinates);
                    if (normalizedCoords) {
                        const offsetCoords = applyOffset(normalizedCoords, angle, constants_1.OFFSET_DISTANCE);
                        const offsetCoordinates = reconstructCoordinates(offsetCoords, location.coordinates);
                        offsetLocations.push(Object.assign(Object.assign({}, location), { coordinates: offsetCoordinates, originalCoordinates: location.coordinates }));
                    }
                });
            }
        });
        return offsetLocations;
    }, [
        locations,
        applyOffset,
        normalizeCoordinates,
        reconstructCoordinates,
        showMultipleMarkersForSameLocation
    ]);
};
exports.useOffsetLocations = useOffsetLocations;
//# sourceMappingURL=useOffsetLocations.js.map